import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Typography,
  Upload
} from "antd";
import { RobotOutlined, UploadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import { addDocument, createUploadLink, extractDocument, updateDocument } from "./api";
import {
  centsToEuro,
  decimalInputProps,
  euroToCents,
  formatDay,
  formatEuro,
  warningText
} from "./format";

const ACCEPT = "application/pdf,image/jpeg,image/png,image/webp";
const DATE_FIELDS = ["issuedAt", "validFrom", "validUntil"];

const toPicker = value => (value ? dayjs(value) : null);
const fromPicker = value => (value ? value.format("YYYY-MM-DD") : "");

/** The fields a document type asks for (number, issuer, dates, sum insured). */
const DocumentFields = ({ type, highlight = [] }) => {
  const { t, i18n } = useTranslation("dashboard");
  if (!type) return null;
  const status = key => (highlight.includes(key) ? "error" : undefined);
  return (
    <Row gutter={[12, 0]}>
      <Col xs={24} md={12}>
        <Form.Item
          name="number"
          label={t("Number")}
          validateStatus={status("number")}
          required={type.needsNumber}
        >
          <Input maxLength={100} />
        </Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item
          name="issuer"
          label={type.issuerLabel || t("Issued by")}
          validateStatus={status("issuer")}
          required={type.multiple && Boolean(type.issuerLabel)}
        >
          <Input maxLength={200} />
        </Form.Item>
      </Col>
      <Col xs={12} md={8}>
        <Form.Item name="issuedAt" label={t("Issue date")} validateStatus={status("issuedAt")}>
          <DatePicker className="sc-full" format="DD.MM.YYYY" />
        </Form.Item>
      </Col>
      <Col xs={12} md={8}>
        <Form.Item name="validFrom" label={t("Valid from")} validateStatus={status("validFrom")}>
          <DatePicker className="sc-full" format="DD.MM.YYYY" />
        </Form.Item>
      </Col>
      {!type.noExpiry && (
        <Col xs={24} md={8}>
          <Form.Item
            name="validUntil"
            label={t("Valid until")}
            validateStatus={status("validUntil")}
            required={type.needsValidUntil}
            extra={
              !type.needsValidUntil && type.maxAgeMonths
                ? t("If no end date is printed: accepted for {{n}} months from the issue date.", {
                    n: type.maxAgeMonths
                  })
                : null
            }
          >
            <DatePicker className="sc-full" format="DD.MM.YYYY" />
          </Form.Item>
        </Col>
      )}
      {type.hasSumInsured && (
        <Col xs={24} md={12}>
          <Form.Item name="sumInsured" label={t("Sum insured")}>
            <InputNumber
              className="sc-full"
              min={0}
              precision={2}
              addonAfter="€"
              {...decimalInputProps(i18n.language)}
            />
          </Form.Item>
        </Col>
      )}
      <Col xs={24}>
        <Form.Item name="note" label={t("Note")}>
          <Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} maxLength={1000} />
        </Form.Item>
      </Col>
    </Row>
  );
};

const valuesToBody = values => ({
  number: values.number || "",
  issuer: values.issuer || "",
  note: values.note || "",
  ...Object.fromEntries(DATE_FIELDS.map(key => [key, fromPicker(values[key])])),
  sumInsured: values.sumInsured === undefined ? undefined : euroToCents(values.sumInsured)
});

const docToValues = doc => ({
  number: doc.number,
  issuer: doc.issuer,
  note: doc.note,
  sumInsured: doc.sumInsured ? centsToEuro(doc.sumInsured) : undefined,
  ...Object.fromEntries(DATE_FIELDS.map(key => [key, toPicker(doc[key])]))
});

/** Upload a scan (optionally read by AI) and, when the fields are complete, confirm it. */
export const UploadDocumentModal = ({ open, sub, meta, presetType, onClose, onSaved }) => {
  const { t } = useTranslation("dashboard");
  const [form] = Form.useForm();
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [highlight, setHighlight] = useState([]);
  const typeKey = Form.useWatch("type", form);
  const type = (meta?.documentTypes || []).find(x => x.key === typeKey);

  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue({
        type: presetType,
        extract: Boolean(meta?.ai.configured),
        confirm: false
      });
      setFile(null);
      setHighlight([]);
    }
  }, [open, presetType]);

  const submit = async values => {
    setBusy(true);
    try {
      const res = await addDocument(
        sub._id,
        {
          type: values.type,
          ...valuesToBody(values),
          extract: values.extract && file ? 1 : undefined,
          confirm: values.confirm ? 1 : undefined
        },
        file
      );
      if (res.data.ai && !res.data.ai.ok)
        Success(t("Saved. The document could not be read automatically."));
      else Success(t("Saved"));
      onSaved(res.data.data, res.data.document);
    } catch (error) {
      setHighlight(error?.response?.data?.missingFields || []);
      generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      rootClassName="subcontractors-page"
      open={open}
      width={720}
      title={t("Add document")}
      okText={t("Save")}
      cancelText={t("Cancel")}
      confirmLoading={busy}
      onOk={() => form.submit()}
      onCancel={onClose}
    >
      <Form form={form} layout="vertical" onFinish={submit}>
        <Form.Item name="type" label={t("Document type")} rules={[{ required: true }]}>
          <Select
            showSearch
            optionFilterProp="label"
            options={(meta?.documentTypes || []).map(x => ({ value: x.key, label: x.label }))}
          />
        </Form.Item>
        <Form.Item label={t("Scan (PDF or photo)")}>
          <Upload
            accept={ACCEPT}
            maxCount={1}
            beforeUpload={selected => {
              setFile(selected);
              return false;
            }}
            onRemove={() => setFile(null)}
            fileList={file ? [{ uid: "1", name: file.name, status: "done" }] : []}
          >
            <Button icon={<UploadOutlined />}>{t("Choose file")}</Button>
          </Upload>
        </Form.Item>
        <DocumentFields type={type} highlight={highlight} />
        <Space direction="vertical">
          <Form.Item name="extract" valuePropName="checked" noStyle>
            <Checkbox disabled={!meta?.ai.configured || !file}>
              {meta?.ai.configured
                ? t("Read number and validity from the scan (AI suggestion, you confirm)")
                : t("Reading documents automatically is not configured on this server.")}
            </Checkbox>
          </Form.Item>
          {meta?.can.update && (
            <Form.Item name="confirm" valuePropName="checked" noStyle>
              <Checkbox>{t("I checked the document, confirm it now")}</Checkbox>
            </Form.Item>
          )}
        </Space>
      </Form>
    </Modal>
  );
};

/** Review an uploaded document: compare with the scan, take over the AI suggestion, confirm. */
export const ReviewDocumentModal = ({ open, sub, doc, meta, fileUrl, onClose, onSaved }) => {
  const { t, i18n } = useTranslation("dashboard");
  const [form] = Form.useForm();
  const [busy, setBusy] = useState("");
  const [highlight, setHighlight] = useState([]);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const type = (meta?.documentTypes || []).find(x => x.key === doc?.type);
  const suggestion = doc?.extraction?.status === "done" ? doc.extraction.fields : null;

  useEffect(() => {
    if (open && doc) {
      form.resetFields();
      form.setFieldsValue(docToValues(doc));
      setHighlight(doc.missingFields || []);
      setRejecting(false);
      setReason("");
    }
  }, [open, doc?._id, doc?.extraction?.at]);

  const run = async (label, action) => {
    setBusy(label);
    try {
      await action();
    } catch (error) {
      setHighlight(error?.response?.data?.missingFields || highlight);
      generalErrorAlert(error);
    } finally {
      setBusy("");
    }
  };

  const save = action =>
    run(action, async () => {
      const values = await form.validateFields();
      const res = await updateDocument(sub._id, doc._id, { action, ...valuesToBody(values) });
      Success(action === "confirm" ? t("Document confirmed") : t("Saved"));
      onSaved(res.data.data);
    });

  const reject = () =>
    run("reject", async () => {
      const res = await updateDocument(sub._id, doc._id, { action: "reject", reason });
      onSaved(res.data.data);
    });

  const readWithAi = () =>
    run("extract", async () => {
      const res = await extractDocument(sub._id, doc._id);
      onSaved(res.data.data, doc._id);
    });

  const applySuggestion = () => {
    form.setFieldsValue({
      number: suggestion.number || form.getFieldValue("number"),
      issuer: suggestion.issuer || form.getFieldValue("issuer"),
      ...Object.fromEntries(
        DATE_FIELDS.filter(key => suggestion[key]).map(key => [key, dayjs(suggestion[key])])
      ),
      ...(suggestion.sumInsured ? { sumInsured: centsToEuro(suggestion.sumInsured) } : {})
    });
  };

  if (!doc) return null;
  const editable = doc.state !== "confirmed";
  return (
    <Modal
      rootClassName="subcontractors-page"
      open={open}
      width={760}
      title={type?.label || doc.type}
      onCancel={onClose}
      footer={
        editable ? (
          <Space wrap>
            <Button onClick={() => setRejecting(true)} disabled={Boolean(busy)}>
              {t("Reject")}
            </Button>
            <Button onClick={() => save("save")} loading={busy === "save"}>
              {t("Save")}
            </Button>
            <Button type="primary" onClick={() => save("confirm")} loading={busy === "confirm"}>
              {t("Confirm")}
            </Button>
          </Space>
        ) : (
          <Button onClick={onClose}>{t("Close")}</Button>
        )
      }
    >
      {doc.hasFile && (
        <Typography.Paragraph>
          <a href={fileUrl} target="_blank" rel="noopener noreferrer">
            {t("Open the scan")} ({doc.fileName || "PDF"})
          </a>
        </Typography.Paragraph>
      )}
      {editable && meta?.ai.configured && doc.hasFile && !suggestion && (
        <Button
          className="mb-3"
          icon={<RobotOutlined />}
          loading={busy === "extract"}
          onClick={readWithAi}
        >
          {t("Read with AI")}
        </Button>
      )}
      {doc.extraction?.status === "failed" && (
        <Alert
          className="mb-3"
          type="info"
          showIcon
          message={t("The document could not be read automatically. Please type the values.")}
        />
      )}
      {editable && suggestion && (
        <Alert
          className="mb-3"
          type="info"
          showIcon
          icon={<RobotOutlined />}
          message={t("AI suggestion – please compare with the scan")}
          description={
            <>
              <Descriptions size="small" column={1}>
                {suggestion.number && (
                  <Descriptions.Item label={t("Number")}>{suggestion.number}</Descriptions.Item>
                )}
                {suggestion.issuer && (
                  <Descriptions.Item label={t("Issued by")}>{suggestion.issuer}</Descriptions.Item>
                )}
                {suggestion.validFrom && (
                  <Descriptions.Item label={t("Valid from")}>
                    {formatDay(suggestion.validFrom)}
                  </Descriptions.Item>
                )}
                {suggestion.validUntil && (
                  <Descriptions.Item label={t("Valid until")}>
                    {formatDay(suggestion.validUntil)}
                  </Descriptions.Item>
                )}
                {suggestion.sumInsured ? (
                  <Descriptions.Item label={t("Sum insured")}>
                    {formatEuro(suggestion.sumInsured, i18n.language)}
                  </Descriptions.Item>
                ) : null}
              </Descriptions>
              {(doc.extraction.warnings || []).map(w => (
                <div key={w} className="sc-small">
                  {warningText(t, w)}
                </div>
              ))}
              <Button size="small" className="mt-2" onClick={applySuggestion}>
                {t("Apply suggestion")}
              </Button>
            </>
          }
        />
      )}
      {doc.state === "rejected" && (
        <Alert className="mb-3" type="warning" showIcon message={doc.rejectReason} />
      )}
      <Form form={form} layout="vertical" disabled={!editable}>
        <DocumentFields type={type} highlight={highlight} />
      </Form>
      {rejecting && (
        <Space.Compact className="sc-full">
          <Input
            placeholder={t("Reason")}
            value={reason}
            maxLength={500}
            onChange={event => setReason(event.target.value)}
          />
          <Button danger disabled={!reason.trim()} loading={busy === "reject"} onClick={reject}>
            {t("Reject")}
          </Button>
        </Space.Compact>
      )}
    </Modal>
  );
};

/** Ask the subcontractor for documents through a secure upload link (14 days). */
export const RequestDocumentsModal = ({ open, sub, meta, onClose, onSaved }) => {
  const { t } = useTranslation("dashboard");
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState("");

  useEffect(() => {
    if (open && sub) {
      setUrl("");
      form.setFieldsValue({
        types: [...sub.compliance.problems, ...sub.compliance.expiring],
        email: sub.email,
        send: Boolean(sub.email)
      });
    }
  }, [open]);

  const submit = async values => {
    setBusy(true);
    try {
      const res = await createUploadLink(sub._id, values);
      setUrl(res.data.url);
      if (res.data.sentTo) Success(t("Request sent to {{email}}", { email: res.data.sentTo }));
      onSaved(res.data.data);
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      rootClassName="subcontractors-page"
      open={open}
      width={640}
      title={t("Request documents")}
      okText={url ? t("Close") : t("Create link")}
      cancelButtonProps={{ style: url ? { display: "none" } : undefined }}
      cancelText={t("Cancel")}
      confirmLoading={busy}
      onOk={() => (url ? onClose() : form.submit())}
      onCancel={onClose}
    >
      {url ? (
        <>
          <Alert
            className="mb-3"
            type="success"
            showIcon
            message={t("The link is valid for 14 days. Uploads wait for your review.")}
          />
          <Typography.Paragraph copyable={{ text: url }} className="sc-break">
            {url}
          </Typography.Paragraph>
        </>
      ) : (
        <Form form={form} layout="vertical" onFinish={submit}>
          <Form.Item name="types" label={t("Documents")} rules={[{ required: true }]}>
            <Select
              mode="multiple"
              optionFilterProp="label"
              options={(meta?.documentTypes || []).map(x => ({ value: x.key, label: x.label }))}
            />
          </Form.Item>
          <Form.Item name="send" valuePropName="checked">
            <Checkbox>{t("Send the link by email")}</Checkbox>
          </Form.Item>
          <Form.Item name="email" label={t("Email")} rules={[{ type: "email" }]}>
            <Input maxLength={200} />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};
